// src/components/professor/ExportSection.js
import React, { useState, useEffect } from "react";
import Preloader from "../../components/Preloader";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
} from "firebase/firestore";
import { getDatabase, ref as rtdbRef, get } from "firebase/database";
import { db } from "../../firebase";
import { getAuth } from "firebase/auth";

export default function ExportSection({ theme }) {
  const [loading, setLoading] = useState(false);
  const [classes, setClasses] = useState([]);
  const [selectedClassId, setSelectedClassId] = useState("");
  const [reportType, setReportType] = useState("both");
  const [sessionsList, setSessionsList] = useState([]);
  const [sessionsData, setSessionsData] = useState({});
  const [selectedSessions, setSelectedSessions] = useState(["ALL"]);

  // Load classes on mount
  useEffect(() => {
    async function fetchClasses() {
      setLoading(true);
      try {
        const user = getAuth().currentUser;
        if (!user) throw new Error("Not logged in");
        const q1 = query(collection(db, "classes"), where("professor_uid", "==", user.uid));
        const q2 = query(collection(db, "classes"), where("access_uid", "array-contains", user.uid));
        const [s1, s2] = await Promise.all([getDocs(q1), getDocs(q2)]);
        const map = new Map();
        s1.forEach(d => map.set(d.id, { id: d.id, ...d.data() }));
        s2.forEach(d => map.has(d.id) || map.set(d.id, { id: d.id, ...d.data() }));
        setClasses(Array.from(map.values()));
      } catch (e) {
        console.error(e);
        alert("Error loading classes.");
      } finally {
        setLoading(false);
      }
    }
    fetchClasses();
  }, []);

  // Load sessions whenever class changes
  useEffect(() => {
    if (!selectedClassId) {
      setSessionsList([]);
      setSessionsData({});
      return;
    }
    async function fetchSessions() {
      setLoading(true);
      try {
        const dbR = getDatabase();
        const snap = await get(rtdbRef(dbR, `sessions/${selectedClassId}`));
        const data = snap.exists() ? snap.val() : {};
        setSessionsData(data);
        setSessionsList(Object.keys(data));
        setSelectedSessions(["ALL"]);
      } catch (e) {
        console.error(e);
        alert("Error loading sessions.");
      } finally {
        setLoading(false);
      }
    }
    fetchSessions();
  }, [selectedClassId]);

  const toggleSession = (sid) => {
    if (sid === 'ALL') {
      return setSelectedSessions(prev => prev.includes('ALL') ? [] : ['ALL']);
    }
    setSelectedSessions(prev => {
      const set = new Set(prev);
      if (set.has('ALL')) set.delete('ALL');
      set.has(sid) ? set.delete(sid) : set.add(sid);
      return Array.from(set);
    });
  };

  const formatSessionLabel = (timestamp) => {
    const dt = new Date(timestamp);
    return dt.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
    });
  };

  const handleExport = async () => {
    if (!selectedClassId) return alert("Select a class");
    const choose = selectedSessions.includes('ALL') || !selectedSessions.length
      ? sessionsList
      : selectedSessions;
    if (!choose.length) return alert("Select at least one session");
    setLoading(true);
    try {
      const dbR = getDatabase();
      const enrollSnap = await getDoc(doc(db, 'enrollments', selectedClassId));
      if (!enrollSnap.exists()) throw new Error("No enrollments");
      const uids = enrollSnap.data().students || [];
      if (!uids.length) throw new Error("No students in class");
      const users = await Promise.all(uids.map(uid => getDoc(doc(db, 'users', uid))));
      const students = users.map(d => d.exists() ? { uid: d.id, ...d.data() } : { uid: d.id });
      const gradesSnap = await getDoc(doc(db, 'grades', selectedClassId));
      const grades = gradesSnap.exists() ? gradesSnap.data() : {};
      // attendance per session
      const attendList = await Promise.all(
        choose.map(sid =>
          get(rtdbRef(dbR, `attendance/${selectedClassId}/${sid}/userIds`))
            .then(s => s.exists() ? s.val() : [])
        )
      );
      const attendance = choose.reduce((acc, sid, i) => {
        acc[sid] = attendList[i];
        return acc;
      }, {});
      // build CSV rows
      const rows = students.map(std => {
        const base = {
          universityId: std.universityId || '',
          firstName: std.firstName || '',
          lastName: std.lastName || '',
        };
        const attended = choose.filter(sid => attendance[sid].includes(std.uid)).length;
        const participation = ((attended/choose.length)*100).toFixed(2);
        let pts=0, max=0;
        if (reportType!=='participation') {
          choose.forEach(sid => {
            const sess = sessionsData[sid] || {};
            const questions = grades.students?.[std.uid]?.[sid]?.points || {};
            pts += Object.values(questions).reduce((a,v)=>a+v,0);
            const totalQ = sess.total_questions || Object.keys(sess.questions||{}).length;
            max += totalQ;
          });
        }
        const gradePct = max>0?((pts/max)*100).toFixed(2):'0.00';
        const record = { ...base };
        if (reportType!=='participation') Object.assign(record, { Points: pts, Total: max, GradePercentage: gradePct });
        if (reportType!=='grades') record.Participation = participation;
        return record;
      });
      const headers = ['universityId','firstName','lastName'];
      if (reportType!=='participation') headers.push('Points','Total','GradePercentage');
      if (reportType!=='grades') headers.push('Participation');
      const csv = [headers.join(',')];
      rows.forEach(r => {
        csv.push(headers.map(h=>{
          const v=r[h]||'';
          return /[,"]/.test(v)?`"${v.replace(/"/g,'""')}"`:v;
        }).join(','));
      });
      const blob = new Blob([csv.join('\n')], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `${classes.find(c=>c.id===selectedClassId).class_name.replace(/\s+/g,'_')}_coursengage_export.csv`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      alert('Export successful');
    } catch(e) {
      console.error(e);
      alert('Export failed: '+e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="p-0" style={{ backgroundColor:'var(--section-bg)', color:'var(--text-color)' }}>
      {loading && <Preloader />}

      {/* Class Selector */}
      <div className="mb-4 rounded-lg border p-4" style={{ backgroundColor: 'var(--card-bg)', borderColor: 'var(--border-color)', boxShadow: 'var(--shadow-sm)' }}>
        <select
          value={selectedClassId}
          onChange={e=>setSelectedClassId(e.target.value)}
          className="w-full rounded-lg border p-3 focus:ring-2 focus:ring-[var(--primary-color)]"
        >
          <option value="">-- Choose Class --</option>
          {classes.map(c=><option key={c.id} value={c.id}>{c.class_name}</option>)}
        </select>
      </div>

      {/* Report Type */}
      <div className="mb-4 flex flex-wrap gap-2 rounded-lg border p-2" style={{ backgroundColor: 'var(--card-bg)', borderColor: 'var(--border-color)', boxShadow: 'var(--shadow-sm)' }}>
        {['grades','participation','both'].map(type=>(
          <button
            key={type}
            onClick={()=>setReportType(type)}
            className={`rounded-lg px-4 py-2 text-sm font-semibold transition ${reportType===type? 'bg-[var(--primary-color)] text-white':'text-[var(--text-color)]'}`}
          >
            {type.charAt(0).toUpperCase()+type.slice(1)}
          </button>
        ))}
      </div>

      {/* Sessions Grid */}
      {sessionsList.length>0 && (
        <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {/* All Sessions Card */}
          <div
            onClick={()=>toggleSession('ALL')}
            className={`cursor-pointer rounded-lg p-4 text-center text-sm font-semibold transition ${selectedSessions.includes('ALL')? 'border-2 border-[var(--primary-color)] bg-[var(--alt-bg)]':'border border-[var(--border-color)] bg-[var(--card-bg)]'}`}
          >
            All Sessions
          </div>
          {sessionsList.map(sid=>{
            const sess = sessionsData[sid] || {};
            const label = formatSessionLabel(sess.start_date_time);
            const selected = selectedSessions.includes(sid);
            return (
              <div
                key={sid}
                onClick={()=>toggleSession(sid)}
                className={`cursor-pointer rounded-lg p-4 text-sm transition ${selected? 'border-2 border-[var(--primary-color)] bg-[var(--alt-bg)]':'border border-[var(--border-color)] bg-[var(--card-bg)]'}`}
              >
                {label}
              </div>
            );
          })}
        </div>
      )}

      <div className="flex justify-end">
        <button
          onClick={handleExport}
          className="w-full rounded-lg px-5 py-2.5 text-sm font-semibold transition hover:opacity-90 sm:w-auto"
          style={{ backgroundColor: 'var(--primary-color)', color: 'white' }}
        >
          Export Data
        </button>
      </div>
    </section>
  );
}
